import React, { useState } from 'react';

/**
 * SOC2RequirementsMatrix component displaying a matrix of SOC2 requirements
 * @returns {JSX.Element}
 */
function SOC2RequirementsMatrix() {
  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState({
    'CC 1.0': false,
    'CC 2.0': true,
    'CC 3.0': false,
    'CC 4.0': true,
    'CC 5.0': true,
    'CC 6.0': true,
    'CC 7.0': true,
    'CC 8.0': true,
    'CC 9.0': true,
    'SD': false
  });

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // SOC2 requirements data
  const requirementsData = [
    {
      section: 'CC 1.0 Control Environment',
      subsections: [
        { id: 'CC 1.1', title: 'COSO Principle 1: Integrity and ethical values', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 1.2', title: 'COSO Principle 2: Board independence and oversight', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 1.3', title: 'COSO Principle 3: Structures, reporting lines, and authorities', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 1.4', title: 'COSO Principle 4: Attracting and retaining competent individuals', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 1.5', title: 'COSO Principle 5: Accountability for internal control', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'CC 2.0 Communication and Information',
      subsections: [
        { 
          id: 'CC 2.1', 
          title: 'COSO Principle 13: Quality information for internal control', 
          requirement: 'The company implements systems to collect, analyze, and utilize relevant, quality information to support the functioning of internal controls', 
          documents: [
            'Control Self-Assessment Implementation:',
            '- Implement annual control self-assessments',
            '- Track corrective actions to completion',
            '- Enforce SLA compliance for identified findings',
            '',
            'Log Management Implementation:',
            '- Enable VPC Flow Logs',
            '- Enable CloudTrail',
            '- Link cloud infrastructure to monitoring platform',
            '- Restrict access to logging buckets',
            '- Enable S3 server access logs',
            '- Retain server logs for 365 days',
            '',
            'Vulnerability Management Implementation:',
            '- Address high vulnerabilities in packages (GitHub/GitLab)',
            '- Enable vulnerability scanning (AWS)',
            '- Enable vulnerability scanning (GitHub/GitLab)',
            '- Address critical vulnerabilities (AWS Inspector)',
            '- Address critical, high, medium and low vulnerabilities in repos'
          ], 
          responsibility: 'Kolega' 
        },
        { 
          id: 'CC 2.2', 
          title: 'COSO Principle 14: Internal communication of control information', 
          requirement: 'The company internally communicates information necessary for internal controls to function, including objectives and responsibilities', 
          documents: [
            'Incident Response Policies Implementation:',
            '- Develop and maintain an approved Incident Response Plan',
            '- Ensure all personnel acknowledge and agree to the Incident Response Plan',
            '- Document security and privacy incident response procedures',
            '- Communicate procedures to all authorized users'
          ], 
          responsibility: 'Client' 
        },
        { id: 'CC 2.3', title: 'COSO Principle 15: External communication on control matters', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'CC 3.0 Risk Assessment',
      subsections: [
        { id: 'CC 3.1', title: 'COSO Principle 6: Clarity in objectives for risk identification', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 3.2', title: 'COSO Principle 7: Risk identification and management', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 3.3', title: 'COSO Principle 8: Fraud risk assessment', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 3.4', title: 'COSO Principle 9: Assessment of impactful changes', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'CC 4.0 Monitoring Activities',
      subsections: [
        { 
          id: 'CC 4.1', 
          title: 'COSO Principle 16: Evaluation of internal control', 
          requirement: 'The company selects, develops, and performs ongoing evaluations to determine whether internal controls are functioning', 
          documents: [
            'Vulnerability Scanning Implementation:',
            '- Perform quarterly host-based vulnerability scans on all external-facing systems',
            '- Enable vulnerability scanning in AWS and GitHub/GitLab repositories',
            '- Track remediation of critical and high vulnerabilities',
            '- Address vulnerabilities of all severities (critical, high, medium, low) in code repositories',
            '- Implement AWS Inspector for additional vulnerability detection',
            '- Generate regular vulnerability assessment reports'
          ], 
          responsibility: 'Kolega' 
        },
        { id: 'CC 4.2', title: 'COSO Principle 17: Communication of control deficiencies', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'CC 5.0 Control Activities',
      subsections: [
        { id: 'CC 5.1', title: 'COSO Principle 10: Development of control activities', requirement: '', documents: '', responsibility: '' },
        { 
          id: 'CC 5.2', 
          title: 'COSO Principle 11: Technology control activities', 
          requirement: 'The company selects and develops general control activities over technology to support the achievement of objectives', 
          documents: [
            'SDLC Implementation:',
            '- Develop and maintain a formal Systems Development Life Cycle methodology',
            '- Document procedures for development, acquisition, and implementation',
            '- Include procedures for regular and emergency changes',
            '- Establish formal maintenance processes for systems',
            '- Ensure personnel acknowledge and agree to Secure Development Policy',
            '',
            'Access Control Implementation:',
            '- Develop and maintain an approved Access Control Policy',
            '- Document procedures for adding new users',
            '- Document procedures for modifying existing users',
            '- Document procedures for removing user access',
            '- Establish Information Security Roles and Responsibilities',
            '- Ensure personnel acknowledge all related policies'
          ], 
          responsibility: 'Kolega' 
        },
        { 
          id: 'CC 5.3', 
          title: 'COSO Principle 12: Deployment of control activities', 
          requirement: 'The company implements control activities through policies and procedures', 
          documents: [
            'Change Management Implementation:',
            '- Require authorization for all software and infrastructure changes',
            '- Formally document all changes',
            '- Test changes before implementation',
            '- Review and approve changes prior to deployment',
            '- Implement Operations Security Policy with change management provisions',
            '- Ensure application changes are reviewed',
            '- Enforce separation of duties (author ≠ reviewer)',
            '- Implement branch protection rules for administrators',
            '- Set repository visibility to private'
          ], 
          responsibility: 'Kolega' 
        }
      ]
    },
    {
      section: 'CC 6.0 Logical and Physical Access Controls',
      subsections: [
        { 
          id: 'CC 6.1', 
          title: 'Logical access security implementation', 
          requirement: 'The company implements logical access security software, infrastructure, and architectures to prevent or detect unauthorized access', 
          documents: [
            'Production Asset Management:',
            '- Maintain formal inventory of production system assets',
            '- Include descriptions for all inventory items',
            '- Assign active owners to all assets',
            '- Track resources containing user data',
            '',
            'Production Deployment Controls:',
            '- Restrict migration to production to authorized personnel',
            '- Implement Operations Security Policy and Secure Development Policy',
            '- Ensure application changes are reviewed',
            '- Enforce separation of duties (author ≠ reviewer)',
            '- Implement branch protection rules in GitLab',
            '- Set repository visibility to private',
            '- Implement version control system',
            '',
            'Authentication Controls:',
            '- Enforce unique production database authentication (SSH keys)',
            '- Implement MFA on GitLab',
            '- Implement MFA on infrastructure providers',
            '- Review AWS accounts regularly',
            '- Enable MFA on all user accounts',
            '',
            'Data Encryption:',
            '- Encrypt datastores with sensitive customer data at rest',
            '- Encrypt DynamoDB Tables',
            '- Encrypt MongoDB Atlas clusters',
            '',
            'Account Security:',
            '- Enforce unique username/password or SSH keys for all systems',
            '- Manage permissions via groups',
            '- Utilize service accounts appropriately',
            '- Ensure root infrastructure account remains unused',
            '- Disable old infrastructure accounts',
            '- Avoid direct policy attachment to user accounts',
            '',
            'Network Security:',
            '- Restrict firewall access to authorized users',
            '- Restrict EC2 instance public ports',
            '- Implement network ACLs and security groups',
            '- Deny public SSH access',
            '- Filter unwanted traffic',
            '- Configure firewall to default-deny traffic',
            '- Restrict RDS instance IP access',
            '- Implement network segmentation to protect customer data'
          ], 
          responsibility: 'Kolega' 
        },
        { id: 'CC 6.2', title: 'User registration and access control', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 6.3', title: 'Role-based access management', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 6.4', title: 'Physical access restrictions', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 6.5', title: 'Decommissioning of logical and physical assets', requirement: '', documents: '', responsibility: '' },
        { 
          id: 'CC 6.6', 
          title: 'External threat protection', 
          requirement: 'The company implements boundary protection systems and mechanisms to prevent and detect external threats', 
          documents: [
            'Intrusion Detection Implementation:',
            '- Implement and maintain an intrusion detection system',
            '- Enable CloudTrail for AWS environments',
            '- Provide continuous monitoring of network activity',
            '- Configure early detection of potential security breaches',
            '- Document system installation and configuration',
            '',
            'Data Encryption in Transit:',
            '- Implement and maintain a Cryptography Policy',
            '- Configure load balancers to redirect HTTP to HTTPS',
            '- Enable SSL/TLS on all admin interfaces',
            '- Use only strong SSL/TLS ciphers',
            '- Regularly verify SSL configuration has no known issues',
            '- Monitor SSL/TLS certificate expiration',
            '- Enforce SSL/TLS on company website',
            '',
            'Firewall Management:',
            '- Review firewall rulesets at least annually',
            '- Track required firewall changes to completion',
            '- Ensure AWS ECS tasks have network ACLs or security groups',
            '- Deny public SSH access',
            '- Configure firewalls to filter unwanted traffic',
            '- Set firewall defaults to disallow traffic',
            '',
            'System Hardening:',
            '- Document network and system hardening standards',
            '- Base standards on industry best practices',
            '- Review hardening standards at least annually',
            '- Implement Operations Security Policy',
            '- Configure security groups and ACLs',
            '- Review AWS accounts regularly'
          ], 
          responsibility: 'Kolega' 
        },
        { id: 'CC 6.7', title: 'Information transmission security', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 6.8', title: 'Malware prevention and detection', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'CC 7.0 System Operations',
      subsections: [
        { 
          id: 'CC 7.1', 
          title: 'Vulnerability detection and monitoring', 
          requirement: 'The company implements infrastructure and procedures for vulnerability detection, monitoring, and assessment', 
          documents: [
            'Configuration Management Implementation:',
            '- Implement and maintain configuration management procedures',
            '- Develop and approve Operations Security Policy',
            '- Deploy CI/CD system for consistent deployments',
            '- Ensure system configurations are deployed consistently',
            '- Document configuration management processes',
            '',
            'Change Management Implementation:',
            '- Require authorization for all software and infrastructure changes',
            '- Formally document all changes',
            '- Test changes before implementation',
            '- Review and approve changes prior to deployment',
            '- Implement Operations Security Policy with change management provisions',
            '- Ensure application changes are reviewed',
            '- Enforce separation of duties (author ≠ reviewer)',
            '- Implement branch protection rules for administrators in GitLab',
            '- Set GitLab repository visibility to private'
          ], 
          responsibility: 'Kolega' 
        },
        { 
          id: 'CC 7.2', 
          title: 'System anomaly monitoring', 
          requirement: 'The company implements mechanisms and procedures to detect, report, and act upon system anomalies', 
          documents: [
            'Infrastructure Monitoring Implementation:',
            '- Implement infrastructure monitoring tools for systems and performance',
            '- Configure alerts for predefined thresholds',
            '- Monitor load balancer metrics:',
            '  • HTTP to HTTPS redirects',
            '  • Unhealthy host counts',
            '  • Latency',
            '  • Server errors',
            '- Configure database monitoring:',
            '  • MongoDB Atlas cluster storage autoscaling',
            '  • MongoDB Atlas clusters on dedicated nodes',
            '  • SQL database CPU utilization',
            '  • SQL database freeable memory',
            '  • Database I/O performance',
            '  • SQL database free storage space',
            '  • NoSQL database read capacity',
            '  • NoSQL database write capacity',
            '- Monitor server resources:',
            '  • Server CPU utilization (AWS)',
            '- Generate alerts when thresholds are exceeded',
            '- Document monitoring procedures and threshold values'
          ], 
          responsibility: 'Kolega' 
        },
        { id: 'CC 7.3', title: 'Security event evaluation', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 7.4', title: 'Incident response program', requirement: '', documents: '', responsibility: '' },
        { id: 'CC 7.5', title: 'Security incident recovery', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'CC 8.0 Change Management',
      subsections: [
        { 
          id: 'CC 8.1', 
          title: 'Change management process', 
          requirement: 'The company authorizes, designs, develops, implements, tests, approves, and documents changes', 
          documents: [
            'Change Management Implementation:',
            '- Require authorization for all software and infrastructure changes',
            '- Formally document all changes',
            '- Test changes before implementation',
            '- Review and approve changes prior to deployment',
            '- Implement Operations Security Policy with change management provisions',
            '- Ensure application changes are reviewed',
            '- Enforce separation of duties (author ≠ reviewer)',
            '- Implement branch protection rules for administrators in GitLab',
            '- Set GitLab repository visibility to private'
          ], 
          responsibility: 'Kolega' 
        }
      ]
    },
    {
      section: 'CC 9.0 Risk Mitigation',
      subsections: [
        { 
          id: 'CC 9.1', 
          title: 'Business disruption risk mitigation', 
          requirement: 'The company identifies, selects, and develops risk mitigation activities for risks arising from potential business disruptions', 
          documents: [
            'Business Continuity & Disaster Recovery Implementation:',
            '- Develop and maintain Business Continuity and Disaster Recovery Plans',
            '- Include communication plans for information security continuity',
            '- Address scenarios involving unavailability of key personnel',
            '- Ensure all personnel acknowledge and agree to the BC/DR Plan',
            '- Document recovery procedures for critical systems',
            '- Define roles and responsibilities during continuity events',
            '- Establish communication protocols during disruptions'
          ], 
          responsibility: 'Client' 
        },
        { id: 'CC 9.2', title: 'Vendor and partner risk management', requirement: '', documents: '', responsibility: '' }
      ]
    },
    {
      section: 'SD - SOC 2 Supporting Compliance Documentation',
      subsections: [
        { id: 'SD', title: 'SOC 2 System and commitments description for audit', requirement: '', documents: '', responsibility: '' }
      ]
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">SOC2 Requirements Matrix</h1>
      <p className="mb-6">
        This matrix outlines the SOC2 requirements and responsibilities between Kolega and clients.
        Last updated: {new Date().toLocaleDateString()}
      </p>

      {requirementsData.map((section) => (
        <div key={section.section} className="mb-6 border rounded-lg overflow-hidden">
          <div 
            className="bg-gray-100 p-4 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection(section.section.split(' ')[0] + ' ' + section.section.split(' ')[1])}
          >
            <h2 className="text-lg font-semibold">{section.section}</h2>
            <span>{expandedSections[section.section.split(' ')[0] + ' ' + section.section.split(' ')[1]] ? '▼' : '▶'}</span>
          </div>
          
          {expandedSections[section.section.split(' ')[0] + ' ' + section.section.split(' ')[1]] && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Subsection</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Requirement</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-3/5">Required Document(s)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {section.subsections.map((subsection) => (
                    <tr key={subsection.id}>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        <div className="font-bold">{subsection.id}</div>
                        <div className="text-xs mt-1">{subsection.title}</div>
                        {subsection.responsibility && (
                          <div className="mt-2">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              subsection.responsibility === 'Kolega' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {subsection.responsibility}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {subsection.requirement}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {Array.isArray(subsection.documents) ? (
                          <div className="space-y-3">
                            {subsection.documents.map((doc, index) => {
                              if (doc.endsWith(':')) {
                                // Section headers
                                return (
                                  <div key={index} className="font-semibold text-gray-700 mt-3 border-b pb-1 flex justify-between items-center">
                                    <span>{doc}</span>
                                    {/* Small progress indicator */}
                                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                      <div className="bg-green-500 h-full rounded-full" style={{width: '60%'}}></div>
                                    </div>
                                  </div>
                                );
                              } else if (doc.startsWith('-')) {
                                // List items as card components
                                return (
                                  <div key={index} className="bg-gray-50 rounded-md p-2 border border-gray-100 hover:border-purple-200 transition-colors cursor-pointer">
                                    <div className="flex items-start">
                                      <span className="text-purple-500 mr-2 mt-0.5 flex-shrink-0">•</span>
                                      <span className="flex-grow">{doc.substring(2)}</span>
                                    </div>
                                  </div>
                                );
                              } else if (doc === '') {
                                // Empty line for spacing
                                return <div key={index} className="h-1"></div>;
                              } else {
                                // Regular text
                                return <div key={index} className="mb-1">{doc}</div>;
                              }
                            })}
                          </div>
                        ) : (
                          subsection.documents
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-2">Responsibility Legend:</h3>
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <span className="w-4 h-4 inline-block rounded-full bg-purple-100 mr-2"></span>
            <span className="text-sm">Kolega Responsibility</span>
          </div>
          <div className="flex items-center">
            <span className="w-4 h-4 inline-block rounded-full bg-amber-100 mr-2"></span>
            <span className="text-sm">Client Responsibility</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SOC2RequirementsMatrix;
